import { useRetainedRef } from "@appretio/shared";
import type {
  ColumnState,
  FilterModel,
  GridApi,
  GridState
} from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import { type RefObject, useState } from "react";
import { type ForecastTableControlValues } from "../../components/tableControls/forecastTableControlValues";
import {
  captureColumnState,
  PERSISTED_COLUMN_STATE_FIELDS
} from "../../savedViews/captureViewPayload";
import type { ResolvedViewState } from "../../savedViews/resolveViewRows";
import {
  CALENDAR_VIEW_TO_TYPE,
  YEAR_COL_IDS,
  yearColId
} from "../../savedViews/resolveViewRows";
import { type CubePage, pageScope } from "../useCubeSelectionState";
import { useRetainedGridRows } from "./useRetainedGridRows";

/**
 * The fields a saved view keeps of a column, plus its sort: a view stores
 * sorting as intent (``sortBy``), the stash as the live state.
 */
const STASHED_COLUMN_STATE_FIELDS = [
  ...PERSISTED_COLUMN_STATE_FIELDS,
  "sort",
  "sortIndex"
] as const;

// The view resolves its year filter onto the fiscal year column only; a
// filter on the gregorian ``year`` column is the planner's (``resolveViewRows``).
const VIEW_YEAR_COL_IDS: readonly string[] = [yearColId("inventory_454")];

/** A filter model without the given columns. */
function omitFilterColumns(
  model: FilterModel | null | undefined,
  colIds: readonly string[]
): FilterModel {
  return Object.fromEntries(
    Object.entries(model ?? {}).filter(([colId]) => !colIds.includes(colId))
  );
}

/** Column layout + filter model captured from the grid at one point in time. */
interface GridStateSnapshot {
  /** The user-chosen column fields plus sort ({@link STASHED_COLUMN_STATE_FIELDS}). */
  columnState: ColumnState[];
  /**
   * AG Grid filter model — per-column filter values keyed by colId. Absent
   * since a view was applied ({@link layoutOnly}): the view replaced the
   * model, and a restore leaves the live one alone until the next capture.
   */
  filterModel?: ReturnType<GridApi["getFilterModel"]>;
}

/** The snapshot without what a view apply replaces: filters and sort. */
function layoutOnly(stashed: GridStateSnapshot): GridStateSnapshot {
  return {
    columnState: stashed.columnState.map(
      (column) =>
        Object.fromEntries(
          Object.entries(column).filter(
            ([field]) => field !== "sort" && field !== "sortIndex"
          )
        ) as ColumnState
    )
  };
}

/**
 * Calendar family for grid-state stashing. "Inv 454" and "Fin greg" share
 * fiscal column ids (year column ``fiscal_year_name``); "Greg cal year"
 * swaps it for ``year``, so its state is stashed separately and swapped
 * back in when the user returns.
 */
type CalendarFamily = "fiscal" | "gregorian";

function calendarFamily(
  calendarView: ForecastTableControlValues["calendarView"]
): CalendarFamily {
  return CALENDAR_VIEW_TO_TYPE[calendarView] === "gregorian"
    ? "gregorian"
    : "fiscal";
}

/**
 * Folds the columns both families share — every column but the year one,
 * which is the view's — from `from` into `into`. A shared filter the user
 * removed is removed from the target too, rather than an older stashed one
 * resurfacing.
 */
function mergeCrossFamilyState(
  from: GridStateSnapshot,
  into: GridStateSnapshot | undefined
): GridStateSnapshot {
  const shared = new Map(
    from.columnState
      .filter((column) => !YEAR_COL_IDS.includes(column.colId))
      .map((column) => [column.colId, column])
  );
  const columnState = into
    ? into.columnState.map((column) => shared.get(column.colId) ?? column)
    : [...shared.values()];
  if (!from.filterModel) return { columnState };
  return {
    columnState,
    filterModel: {
      ...Object.fromEntries(
        Object.entries(into?.filterModel ?? {}).filter(
          ([colId]) => colId === "year"
        )
      ),
      ...omitFilterColumns(from.filterModel, YEAR_COL_IDS)
    }
  };
}

/** Whether a stash names every column the grid holds, and no other. */
function describesGrid(stashed: GridStateSnapshot, api: GridApi): boolean {
  const liveIds = new Set(api.getColumnState().map((column) => column.colId));
  return (
    stashed.columnState.length === liveIds.size &&
    stashed.columnState.every((column) => liveIds.has(column.colId))
  );
}

/**
 * Puts a stashed snapshot back on the grid, leaving the year column's sort
 * and the fiscal year filter to the view.
 */
function applySnapshot(api: GridApi, stashed: GridStateSnapshot) {
  api.applyColumnState({
    state: stashed.columnState.map((column) =>
      YEAR_COL_IDS.includes(column.colId)
        ? { ...column, sort: undefined, sortIndex: undefined }
        : column
    ),
    // The cross-family merge into a family the user has not visited
    // stashes the shared columns only, and AG Grid orders a partial
    // state first — pushing the year column behind Producer.
    applyOrder: describesGrid(stashed, api)
  });
  if (!stashed.filterModel) return;
  const live = api.getFilterModel();
  api.setFilterModel({
    ...omitFilterColumns(stashed.filterModel, VIEW_YEAR_COL_IDS),
    ...Object.fromEntries(
      VIEW_YEAR_COL_IDS.filter((colId) => colId in live).map((colId) => [
        colId,
        live[colId]
      ])
    )
  });
}

/** Compose the view and retained presentation before the first row render. */
function initialGridState(
  stashed: GridStateSnapshot | undefined,
  view: ResolvedViewState,
  pendingColumns?: ColumnState[] | null
): GridState | undefined {
  if (!stashed) return undefined;
  const columns = new Map(
    (stashed?.columnState ?? []).map((column) => [column.colId, { ...column }])
  );
  for (const column of pendingColumns ?? []) {
    columns.set(column.colId, { ...columns.get(column.colId), ...column });
  }
  const layout = pendingColumns
    ? [
        ...pendingColumns.map((column) => columns.get(column.colId) ?? column),
        ...[...columns.values()].filter(
          (column) =>
            !pendingColumns.some((pending) => pending.colId === column.colId)
        )
      ]
    : [...columns.values()];
  const sorts = new Map(view.sortModel.map((column) => [column.colId, column]));
  for (const column of stashed?.columnState ?? []) {
    if (!YEAR_COL_IDS.includes(column.colId) && column.sort !== undefined)
      sorts.set(column.colId, column);
  }
  return {
    partialColumnState: true,
    ...(layout.length
      ? {
          columnOrder: layout.some((column) =>
            YEAR_COL_IDS.includes(column.colId)
          )
            ? { orderedColIds: layout.map((column) => column.colId) }
            : undefined,
          columnSizing: {
            columnSizingModel: layout.map(({ colId, width, flex }) => ({
              colId,
              width: width ?? undefined,
              flex: flex ?? undefined
            }))
          },
          columnVisibility: {
            hiddenColIds: layout
              .filter((column) => column.hide)
              .map((column) => column.colId)
          },
          columnPinning: {
            leftColIds: layout
              .filter((column) => column.pinned === "left")
              .map((column) => column.colId),
            rightColIds: layout
              .filter((column) => column.pinned === "right")
              .map((column) => column.colId)
          }
        }
      : {}),
    sort: {
      sortModel: [...sorts.values()]
        .filter((column) => column.sort === "asc" || column.sort === "desc")
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map((column) => ({
          colId: column.colId,
          sort: column.sort as "asc" | "desc"
        }))
    },
    filter: {
      filterModel: stashed?.filterModel
        ? {
            ...omitFilterColumns(stashed.filterModel, VIEW_YEAR_COL_IDS),
            ...Object.fromEntries(
              Object.entries(view.filterModel ?? {}).filter(([colId]) =>
                VIEW_YEAR_COL_IDS.includes(colId)
              )
            )
          }
        : (view.filterModel ?? {})
    }
  };
}

/** Handle a `ForecastDataTable` uses to keep its grid state retained. */
export interface GridStateRetention extends ReturnType<
  typeof useRetainedGridRows
> {
  /**
   * Wire into an effect on the table's calendar view. On a calendar-family
   * switch it folds the outgoing family's stash into the target's and
   * restores the target — by the time the effect runs, the grid's own
   * effect has swapped the column defs.
   */
  trackCalendarView(
    calendarView: ForecastTableControlValues["calendarView"]
  ): void;
  /**
   * Wire into the grid's ``onGridReady`` once the grid holds its columns,
   * after the view has been applied: restores the retained state on a fresh
   * grid instance (remount after navigation, or the empty state → first
   * Apply transition).
   */
  onGridReady(
    api: GridApi,
    calendarView: ForecastTableControlValues["calendarView"]
  ): void;
  /**
   * Wire into the page's view apply. A view replaces the filter model and
   * the sort wholesale, so the stash's copies are stale from that moment:
   * dropped here, and a restore before the next capture — a family switch
   * landing with the apply, or the remount after an apply on the empty
   * state — puts the layout back and leaves the view's filters and sort.
   */
  viewApplied(): void;
  /**
   * Wire into the grid's ``onStateUpdated``: keeps the stash current so it
   * is up to date when the grid unmounts. (A snapshot at unmount time is
   * impossible — React has detached the grid ref by the time an owner's
   * cleanup runs.)
   */
  capture(): void;
  /** Initial presentation before AG Grid renders its first rows. */
  initialState(
    calendarView: ForecastTableControlValues["calendarView"],
    view: ResolvedViewState,
    columns?: ColumnState[] | null
  ): GridState | undefined;
}

/** Inputs for {@link useGridStateRetention}. */
interface UseGridStateRetentionOptions {
  /** The page whose grid this is; the stash lives in its retained scope. */
  page: CubePage;
  /** Ref to the AG Grid instance (owned by the page that renders it). */
  gridRef: RefObject<AgGridReact | null>;
}

/**
 * Keeps the grid's user-applied state — column layout, hand-set filters,
 * unsaved edits — alive across the two things AG Grid cannot cover itself:
 *
 * - **calendar-family switches** (fiscal ↔ gregorian): the year column's
 *   colId differs per family, so each family keeps its own stash, swapped on
 *   switch with every other column's state carried across.
 * - **unmount/remount** (navigation, cube deletion → empty state): the stash
 *   lives in the page's retained scope, so ``onGridReady`` puts the state
 *   back together with the retained data from ``useRetainedForecastData``.
 *
 * Everything else needs no machinery: AG Grid maintains state by colId
 * across columnDefs updates — an Apply's intake reset or a WK→MO→WK round
 * trip keeps filters and column state, pinned by a no-retention test on the
 * data table.
 *
 * For what the stash holds versus what the view owns, see
 * ``docs/saved-views-contract.md``'s "Opening and remembering" section.
 * Capture ordering, which the doc doesn't cover: ``stateUpdated`` is
 * debounced (macrotask), while a family switch merges and restores inside
 * the table's post-commit effect — so a capture never lands between the
 * two, and the outgoing family's stash is its last capture.
 */
export function useGridStateRetention({
  page,
  gridRef
}: UseGridStateRetentionOptions): GridStateRetention {
  const scope = pageScope(page);
  const stashRef = useRetainedRef<
    Partial<Record<CalendarFamily, GridStateSnapshot>>
  >(scope, "gridState", {});
  const rows = useRetainedGridRows(page);
  // Retained like the stash it guards: a calendar switch made while the
  // table is unmounted is still a switch on the next mount.
  const familyRef = useRetainedRef<CalendarFamily | undefined>(
    scope,
    "gridStateFamily",
    undefined
  );

  // One stable handle for the component's lifetime: consumers wire its
  // methods into grid callbacks and effects without dependency churn.
  const [retention] = useState<GridStateRetention>(() => {
    function liveApi(): GridApi | undefined {
      const api = gridRef.current?.api;
      return api && !api.isDestroyed() ? api : undefined;
    }

    function snapshot(api: GridApi): GridStateSnapshot {
      return {
        columnState: captureColumnState(api, STASHED_COLUMN_STATE_FIELDS),
        filterModel: api.getFilterModel()
      };
    }

    function restore(api: GridApi, family: CalendarFamily) {
      const stashed = stashRef.current[family];
      if (stashed) applySnapshot(api, stashed);
    }

    // Records the family the grid shows; on a switch, carries the outgoing
    // family's stash across. The table may be unmounted while the calendar
    // tabs change, so the switch is detected from the stash, not the grid.
    function enterFamily(family: CalendarFamily) {
      const previousFamily = familyRef.current;
      familyRef.current = family;
      if (previousFamily === undefined || previousFamily === family) return;
      const outgoing = stashRef.current[previousFamily];
      if (!outgoing) return;
      stashRef.current = {
        ...stashRef.current,
        [family]: mergeCrossFamilyState(outgoing, stashRef.current[family])
      };
    }

    return {
      ...rows,
      trackCalendarView(calendarView) {
        const family = calendarFamily(calendarView);
        if (familyRef.current === family) return;
        enterFamily(family);
        const api = liveApi();
        if (api) restore(api, family);
      },
      onGridReady(api, calendarView) {
        enterFamily(calendarFamily(calendarView));
        restore(api, familyRef.current ?? "fiscal");
      },
      viewApplied() {
        stashRef.current = Object.fromEntries(
          Object.entries(stashRef.current).map(([family, stashed]) => [
            family,
            layoutOnly(stashed)
          ])
        );
      },
      capture() {
        const family = familyRef.current;
        const api = liveApi();
        if (!family || !api) return;
        stashRef.current = { ...stashRef.current, [family]: snapshot(api) };
      },
      initialState(calendarView, view, columns) {
        const family = calendarFamily(calendarView);
        const outgoing =
          familyRef.current && stashRef.current[familyRef.current];
        const stashed =
          outgoing && familyRef.current !== family
            ? mergeCrossFamilyState(outgoing, stashRef.current[family])
            : stashRef.current[family];
        // A shared-only stash must leave the new year's column defaults intact.
        if (
          !stashed?.columnState.some(
            (column) =>
              column.colId === yearColId(CALENDAR_VIEW_TO_TYPE[calendarView])
          )
        )
          return undefined;
        return initialGridState(stashed, view, columns);
      }
    };
  });

  return retention;
}
